import React, { useEffect, useState } from "react";
import { Container, Div, Text } from "../../styles/Common";
import { theme } from "../../styles/theme";
import { Input } from "../../components/Input";
import { Button } from "../../components/Button";
import filterIcon from "../../assets/icons/filter.svg";
import searchIcon from "../../assets/icons/search.svg";
import show from "../../assets/icons/show.svg";
import edit from "../../assets/icons/edit.svg";
import { AddUserModal } from "./components/AddUserModal";
import { Table } from "../../components/Table";
import { Link } from "../../components/Link";
import Checkbox from "../../components/Checkbox";
import { WrapperStatus } from "./styles";
import { FiltersTable } from "./components/FiltersTable";
import { EditUserModal } from "./components/EditUserModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import useGetUsersList from "./hooks/useGetUsersList";
import { Skeleton } from "../../components/Skeleton";
import useDebounce from "../../core/hooks/useDebounce";
import useChangeStatus from "./hooks/useChangeStatus";
import { Alert } from "antd";
import useGetContractorCompanies from "./hooks/useGetContractorCompanies";
import useOptions from "../../core/hooks/useOptions";

export const UserManagement = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const changeStatus = useChangeStatus();
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [dataUsers, setDataUsers] = useState([]);
  const [userInfo, setUserInfo] = useState({
    id: "",
    name: "",
    lastName: "",
    email: "",
    phoneCodeId: 13,
    phoneNumber: "",
    companiesContractorIds: [],
  });
  const [totalItems, setTotalItems] = useState(10);
  const [pages, setPages] = useState({ 
    current: parseInt(searchParams.get('page')) || 1, 
    pageSize: parseInt(searchParams.get('pageSize')) || 10 
  });
  const [sort, setSort] = useState({
    field: searchParams.get('sortField') || "",
    order: searchParams.get('sortOrder') || "",
  });
  const [searchBy, setSearchBy] = useState(searchParams.get('search') || "");
  const searchValue = useDebounce(searchBy, 600);
  const [status, setStatus] = useState(searchParams.get('status') || "");
  const [tableFilters, setTableFilters] = useState({});
  const [tableSorter, setTableSorter] = useState({});
  const getUsers = useGetUsersList(
    pages.current,
    pages.pageSize,
    searchValue,
    status,
    sort.field,
    sort.order,
  );
  const { data, isSuccess, isLoading, refetch } = getUsers;
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const { data: dataContractorCompanies, isSuccess: isSuccessContractorCompanies, refetch: refetchContractorCompanies } = useGetContractorCompanies();
  const [contractorCompaniesOptions, setContractorCompaniesOptions] = useState([]);
  const [selectedAll, setSelectedAll] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const options = useOptions(5);
  const { data: optionsList } = options;
  const [codeOptions, setCodeOptions] = useState([]);

  const updateUrlParams = (newParams) => {
    const currentParams = new URLSearchParams(searchParams);
    Object.entries(newParams).forEach(([key, value]) => {
      if (value && value !== "" && value !== 0) {
        currentParams.set(key, value);
      } else {
        currentParams.delete(key);
      }
    });
    setSearchParams(currentParams);
  };

  useEffect(() => {
    if (optionsList?.groupSettings?.length > 0) {
      setCodeOptions(
        optionsList.groupSettings.map((ele) => ({
          value: ele.id,
          label: ele.code,
        }))
      );
    }
  }, [optionsList]);

  useEffect(() => {
    const statusMap = {
      "7": "Active",
      "8": "Inactive", 
      "6": "Unconfirmed"
    };
    
    if (status) {
      setTableFilters({ statusName: [statusMap[status]] });
    } else {
      setTableFilters({});
    }

    if (sort.field && sort.order) {
      setTableSorter({
        field: sort.field,
        order: sort.order === 'asc' ? 'ascend' : 'descend'
      });
    } else {
      setTableSorter({});
    }
  }, [status, sort]);

  const getBackgroundStatus = (status) => {
    switch (status) {
      case "Active":
        return theme.colors.greenLight;
      case "Inactive":
        return theme.colors.salmon1;
      case "Unconfirmed":
        return theme.colors.yellowLight;
      default:
        return theme.colors.grey3;
    }
  };

  const getColorStatus = (status) => {
    switch (status) {
      case "Active":
        return theme.colors.green;
      case "Inactive":
        return theme.colors.salmon;
      case "Unconfirmed":
        return theme.colors.yellow;
      default:
        return theme.colors.grey1;
    }
  };

  useEffect(() => {
    if (isSuccessContractorCompanies) {
      setContractorCompaniesOptions(dataContractorCompanies.data.map(company => ({
        value: company.id,
        label: company.name,
      })));
    }
  }, [isSuccessContractorCompanies, dataContractorCompanies]);

  const handleEditUser = (info) => {
    setOpenEdit(true);
    setUserInfo({
      id: info.id,
      name: info.name,
      lastName: info.lastName,
      email: info.email,
      phoneCodeId: info.phoneCodeId || 13,
      phoneNumber: info.phoneNumber,
      companiesContractorIds: info.companiesContractor.map((company) => company.id),
    });
  };

  const handleDetailsUser = (id) => {
    const currentParams = new URLSearchParams();
    if (searchBy) currentParams.set('search', searchBy);
    if (pages.current !== 1) currentParams.set('page', pages.current);
    if (pages.pageSize !== 10) currentParams.set('pageSize', pages.pageSize);
    if (sort.field) currentParams.set('sortField', sort.field);
    if (sort.order) currentParams.set('sortOrder', sort.order);
    if (status) currentParams.set('status', status);
    
    const queryString = currentParams.toString();
    const url = queryString ? `/user-management/${id}?${queryString}` : `/user-management/${id}`;
    navigate(url);
  };

  useEffect(() => {
    if (isSuccess) {
      setDataUsers(data?.data?.projects?.items.map((item) => ({
        ...item,
        key: item.id,
        actions: [
          {
            key: `1${item.id}`,
            id: item.id,
            name: "Edit",
            icon: edit,
            onClick: () => handleEditUser(item),
          },
          {
            key: `2${item.id}`,
            id: item.id,
            name: "Details",
            icon: show,
            onClick: () => handleDetailsUser(item.id),
          },
        ]
      })));
      setTotalItems(data?.data?.projects?.totalItems);
    }
  }, [isSuccess, data]);

  const handleAddUser = () => {
    setOpenCreate(true);
  };

  const handleChangeSearch = (event) => {
    const { value } = event.target;
    setPages({ current: 1, pageSize: 10 });
    setSearchBy(value);
    updateUrlParams({ 
      search: value, 
      page: 1, 
      pageSize: 10,
      sortField: "",
      sortOrder: "",
      status: ""
    });
    setForceUpdate(!forceUpdate);
  };

  const handleChangeTable = (pagination, filters, sorter) => {
    const statusMap = {
      "Active": 7,
      "Inactive": 8,
      "Unconfirmed": 6,
    }
    
    const newPages = pagination;
    const newStatus = filters.statusName ? statusMap[filters.statusName] : "";
    const newSort = sorter.order ? 
      { field: sorter.field, order: sorter.order === 'ascend' ? 'asc' : 'desc'} : 
      { field: "", order: ""};
    
    setPages(newPages);
    setStatus(newStatus);
    setSort(newSort);
    
    updateUrlParams({
      page: newPages.current,
      pageSize: newPages.pageSize,
      status: newStatus,
      sortField: newSort.field,
      sortOrder: newSort.order
    });
    
    setForceUpdate(!forceUpdate);
  };

  const handleClose = () => {
    setOpenCreate(false);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
  };

  const handleSelectAll = () => {
    if (selectedAll) {
      setSelectedUserIds([]);
      setSelectedAll(false);
    } else {
      setSelectedUserIds(dataUsers.map(user => user.id));
      setSelectedAll(true);
    }
  };

  const tableColumns = [
    {
      title: "User Name",
      dataIndex: "name",
      key: "name",
      width: 150,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: tableSorter.field === 'name' ? tableSorter.order : null,
    },
    {
      title: "User Last Name",
      dataIndex: "lastName",
      key: "lastName",
      width: 150,
    },
    {
      title: "Email Address",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Contractor Company",
      dataIndex: "companiesContractor",
      key: "companiesContractor",
      render: (companiesContractor) => (
        companiesContractor?.map(company => company.name).join(", ")
      )
    },
    {
      title: "Role",
      dataIndex: "roles",
      key: "roles",
      width: 150,
      render: (roles) => (
        roles?.join(", ")
      )
    },
    {
      title: "Status",
      dataIndex: "statusName",
      key: "statusName",
      render: (statusName) => (
        <WrapperStatus background={getBackgroundStatus(statusName)} color={getColorStatus(statusName)}>
          {statusName}
        </WrapperStatus>
      ),
      filterDropdown: (props) => <FiltersTable {...props} />,
      filterIcon: filtered => (
        <img src={filterIcon} style={{ color: filtered ? theme.colors.salmon : undefined }} />
      ),
      filteredValue: tableFilters.statusName || null,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (actions) => (
        <Div gap="60px">
          {actions.map((action, i) => (
            <Link
              key={i}
              onClick={action.onClick}
              color={theme.colors.salmon}
              icon={
                <img
                  alt={action.name}
                  src={action.icon}
                  style={{ marginRight: "4px" }}
                />
              }
              styles={{
                "&:hover": {
                  background: "transparent",
                },
              }}
            >
              {action.name}
            </Link>
          ))}
        </Div>
      ),
    }
  ];

  const rowSelection = {
    type: "checkbox",
    selectedRowKeys: selectedUserIds,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedUserIds(selectedRowKeys);
      setSelectedAll(selectedRowKeys.length === dataUsers.length);
    },
    renderCell: (checked, record, index, originNode) => (
      <Checkbox
        checked={checked}
        onChange={() => {
          if (checked) {
            setSelectedUserIds(selectedUserIds.filter(id => id !== record.id));
          } else {
            setSelectedUserIds([...selectedUserIds, record.id]);
          }
        }}
      />
    ),
    columnTitle: () => (
      <Checkbox
        checked={selectedAll}
        onChange={handleSelectAll}
      />
    ),
  };

  const handleChangeStatus = (statusId) => {
    changeStatus.reset();
    changeStatus.mutate({ statusId, usersIds: selectedUserIds }, {
      onSuccess: () => {
        setSelectedUserIds([]);
        setSelectedAll(false);
        refetch();
      },
      onError: (err) => {
        setErrorMessage(err.response.data.Errors[0]);
      }
    });
  };

  return (
    <Container>
      <AddUserModal
        openCreate={openCreate}
        handleClose={handleClose}
        refetchUsers={refetch}
        contractorCompaniesOptions={contractorCompaniesOptions}
        refetch={refetchContractorCompanies}
        codeOptions={codeOptions}
      />
      <EditUserModal
        openCreate={openEdit}
        handleClose={handleCloseEdit}
        userInfo={userInfo}
        setUserInfo={setUserInfo}
        refetch={refetch}
        contractorCompaniesOptions={contractorCompaniesOptions}
        codeOptions={codeOptions}
      />
      <Div width="100%" height="55px" justify="space-between" align="center">
        <Div direction="column">
          <Text
            size={theme.fonts.size.h4}
            color={theme.colors.dark}
            weight={theme.fonts.weight.bold}
          >
            User Management
          </Text>
          <Text
            size={theme.fonts.size.sm}
            color={theme.colors.grey1}
            style={{ maxWidth: "445px" }}
          >
            Add, edit, or remove users, update personal information, and control permissions to keep your team organized.
          </Text>
        </Div>
        <Div gap="24px">
          <Input
            placeholder="Search..."
            suffix={<img alt="search" src={searchIcon} />}
            width="263px"
            value={searchBy}
            onChange={handleChangeSearch}
          />
          <Button 
            border={theme.colors.dark} 
            color={theme.colors.dark} 
            background={"transparent"} 
            width="100px"
            onClick={() => handleChangeStatus(7)}
            disabled={selectedUserIds.length === 0}
            loading={changeStatus.isLoading}
          >
            Active
          </Button>
          <Button 
            border={theme.colors.dark}
            color={theme.colors.dark} 
            background={"transparent"} 
            width="100px"
            onClick={() => handleChangeStatus(8)}
            disabled={selectedUserIds.length === 0}
            loading={changeStatus.isLoading}
          >
            Deactive
          </Button>
          <Button background={theme.colors.salmon} onClick={handleAddUser}>
            Add New User
          </Button>
        </Div>
      </Div>
      {errorMessage !== "" && (
        <Div m="10px 0 0 0" justify="flex-end">
          <Alert closable message={`${errorMessage}`} type="error" />
        </Div>
      )}
      <Div m="46px 0 0 0">
        <Table
          rowSelection={rowSelection}
          columns={tableColumns}
          data={dataUsers}
          rowClassName={(record, index) =>
            index % 2 === 0 ? "table-row-light" : "table-row-dark"
          }
          sizerLabels={["Show", "Results"]}
          pagination={{
            defaultCurrent: 1,
            ...pages,
            total: totalItems,
            showSizeChanger: true,
            pageSizeOptions: ["10", "50", "100"],
            locale: {
              items_per_page: "",
            },
            position: ["bottomCenter"],
          }}
          onChange={handleChangeTable}
          locale={{
            emptyText:
              isLoading? (
                <Div justify="center" gap="16px">
                  {[...Array(9)].map((v, idx) => (
                    <Skeleton
                      title={false}
                      paragraph={{
                        rows: 10,
                        width: "100%",
                      }}
                      loading
                      key={idx}
                    />
                  ))}
                </Div>
              ) : (
                "No data"
              ),
          }}
        />
      </Div>
    </Container>
  )
};